import React, { useMemo } from 'react';
import styled from 'styled-components';
import Select, { StylesConfig } from 'react-select';
import { ALL_FIELDS_OPTIONS } from '@/features/editor/widgets/ai-component/inputs/shared/css-declarations';
import { DropdownInputOption } from '@/features/editor/widgets/custom-widget/inputs/shared/dropdown';

export interface AddNewPropertyLayoutProps {
  onCancel?: () => void;
  onAdd?: (properties: string[]) => void;
  existingProperties?: string[];
}

export function AddNewPropertyLayout({
  onAdd,
  onCancel,
  existingProperties,
}: AddNewPropertyLayoutProps) {
  const options = useMemo(() => {
    if (!existingProperties || existingProperties.length === 0) {
      return [...ALL_FIELDS_OPTIONS].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }

    return ALL_FIELDS_OPTIONS.filter(
      (option) => !existingProperties.includes(option.value),
    ).sort((a, b) => a.label.localeCompare(b.label));
  }, [existingProperties]);

  const [selectedProperties, setSelectedProperties] = React.useState<
    DropdownInputOption[]
  >([]);

  const onAddClicked = () => {
    if (onAdd) {
      const properties = selectedProperties.map((option) => option.value);
      onAdd(properties);
    }
  };

  return (
    <Wrapper>
      <Select
        isClearable={false}
        isSearchable
        isMulti
        placeholder="Select CSS properties to be added..."
        options={options}
        styles={selectStyles}
        onChange={setSelectedProperties}
      />
      <ButtonsWrapper>
        <button
          className="update-btn"
          disabled={selectedProperties?.length <= 0}
          type="submit"
          onClick={onAddClicked}
        >
          Add Properties
        </button>
        <button className="cancel-btn" type="submit" onClick={onCancel}>
          Cancel
        </button>
      </ButtonsWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;

  .text {
    font-size: 1.4rem;
    color: rgb(149, 162, 174);
    font-family: 'Inter', sans-serif;
    font-weight: 500;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  button {
    border: 1px solid black;
    padding: 1rem 2rem;
    border-radius: 1rem;
    cursor: pointer;
    user-select: none;

    :disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    &:hover {
      opacity: 0.8;
    }
    &:active {
      opacity: 0.6;
    }

    transition: opacity 0.2s ease-in-out;
  }

  .update-btn {
    background-color: #000;
    border-color: #000;
    color: white;
  }

  .cancel-btn {
    border-color: rgba(149, 162, 174, 0.4);
    color: rgba(149, 162, 174, 1);
    background-color: transparent;
  }
`;

export const selectStyles: StylesConfig<any> = {
  menu: (styles) => ({
    ...styles,
    zIndex: 9999999,
    margin: `0`,
    borderRadius: `0.5rem`,
    padding: `1rem`,
  }),
  menuList: (styles) => ({
    ...styles,
    margin: `0`,
  }),
  control: (styles) => ({
    ...styles,
    border: `none`,
    outline: `none`,
    boxShadow: `none`,
  }),
  input: (styles) => ({
    ...styles,
    fontSize: `1.4rem`,
    color: `rgb(149,162,174)`,
    fontFamily: `Inter, sans-serif`,
    fontWeight: 500,
    border: `none`,
    outline: `none`,
    ':focus': {
      border: `none`,
    },
  }),
  placeholder: (styles) => ({
    ...styles,
    fontSize: `1.4rem`,
    color: `rgb(149,162,174)`,
    fontFamily: `Inter, sans-serif`,
    fontWeight: 500,
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    border: `none`,
    fontSize: `1.4rem`,
    color: `rgb(149,162,174)`,
    fontFamily: `Inter, sans-serif`,
    fontWeight: 500,
    background: isSelected ? `rgba(149, 162, 174, 0.1)` : `transparent`,
    ':hover': {
      color: `rgb(58,61,255)`,
    },
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: `rgba(149, 162, 174, 0.1)`,
    borderRadius: `0.5rem`,
    padding: `0.2rem 0.5rem`,
    fontSize: `1.3rem`,
    fontFamily: `'Inter', sans-serif`,
    textTransform: `uppercase`,
    color: `rgb(149,162,174)`,
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: `0`,
  }),
};
